import { create } from "zustand";
import {
  SkillKind,
  SkillStatus,
  SkillTemplate,
} from "../../services/skills/skillsEndpoints.interface";

export interface StructuredOutputField {
  fieldName: string;
  fieldType: "string" | "number" | "boolean" | "array" | "object";
  description: string;
  required: boolean;
}

type SkillEditorState = {
  name: string;
  displayName: string;
  description: string;
  kind: SkillKind;
  instructionsContent: string;
  priority: number;
  keywords: string[];
  templates: SkillTemplate[];
  structuredOutput: StructuredOutputField[];
  status: string;
  enabled: boolean;
  visibility: "workspace" | "private";
};

type SkillsStore = {
  editor: SkillEditorState;
  isDirty: boolean;
  isGenerating: boolean;
  listSearch: string;
  listStatusFilter: "all" | "active" | "draft" | "archived";
  builderView: "list" | "builder";

  setEditor: (editor: Partial<SkillEditorState>) => void;
  resetEditor: () => void;
  loadSkillIntoEditor: (skill: any) => void;
  setIsDirty: (dirty: boolean) => void;
  setIsGenerating: (generating: boolean) => void;
  setListSearch: (search: string) => void;
  setListStatusFilter: (filter: SkillsStore["listStatusFilter"]) => void;
  setBuilderView: (view: "list" | "builder") => void;
  addTemplate: () => void;
  updateTemplate: (index: number, template: Partial<SkillTemplate>) => void;
  removeTemplate: (index: number) => void;
  addStructuredOutputField: () => void;
  updateStructuredOutputField: (index: number, field: Partial<StructuredOutputField>) => void;
  removeStructuredOutputField: (index: number) => void;
};

const defaultEditor: SkillEditorState = {
  name: "",
  displayName: "",
  description: "",
  kind: SkillKind.CAPABILITY,
  instructionsContent: "",
  priority: 5,
  keywords: [],
  templates: [],
  structuredOutput: [],
  status: SkillStatus.DRAFT,
  enabled: true,
  visibility: "workspace",
};

const useSkillsStore = create<SkillsStore>((set) => ({
  editor: { ...defaultEditor },
  isDirty: false,
  isGenerating: false,
  listSearch: "",
  listStatusFilter: "all",
  builderView: "list" as const,

  setEditor: (partial) =>
    set((state) => ({
      editor: { ...state.editor, ...partial },
      isDirty: true,
    })),

  resetEditor: () =>
    set({ editor: { ...defaultEditor }, isDirty: false }),

  loadSkillIntoEditor: (skill) =>
    set({
      editor: {
        name: skill.skill_name || "",
        displayName: skill.display_name || "",
        description: skill.description || "",
        kind: skill.kind || SkillKind.CAPABILITY,
        instructionsContent: skill.instructions?.instructions_markdown || "",
        priority: skill.priority ?? 5,
        keywords: skill.configuration?.config_data?.triggers?.keywords || [],
        templates: (skill.templates || []).map((t: any) => ({
          id: t.id,
          template_name: t.template_name || "",
          template_type: t.template_type || "markdown",
          template_content: t.template_content || "",
          is_default: t.is_default || false,
        })),
        structuredOutput: (() => {
          if (skill.structured_output) return skill.structured_output;
          if (skill.output_format && typeof skill.output_format === "string") {
            try { return JSON.parse(skill.output_format); } catch { return []; }
          }
          return [];
        })(),
        status: skill.instructions?.status || skill.status || SkillStatus.DRAFT,
        enabled: skill.is_enabled ?? true,
        visibility: skill.visibility || "workspace",
      },
      isDirty: false,
    }),

  setIsDirty: (dirty) => set({ isDirty: dirty }),
  setIsGenerating: (generating) => set({ isGenerating: generating }),
  setListSearch: (search) => set({ listSearch: search }),
  setListStatusFilter: (filter) => set({ listStatusFilter: filter }),
  setBuilderView: (view) => set({ builderView: view }),

  addTemplate: () =>
    set((state) => ({
      editor: {
        ...state.editor,
        templates: [
          ...state.editor.templates,
          { template_name: "", template_type: "markdown", template_content: "" },
        ],
      },
      isDirty: true,
    })),

  updateTemplate: (index, partial) =>
    set((state) => {
      const templates = [...state.editor.templates];
      templates[index] = { ...templates[index], ...partial };
      return { editor: { ...state.editor, templates }, isDirty: true };
    }),

  removeTemplate: (index) =>
    set((state) => ({
      editor: {
        ...state.editor,
        templates: state.editor.templates.filter((_, i) => i !== index),
      },
      isDirty: true,
    })),

  addStructuredOutputField: () =>
    set((state) => ({
      editor: {
        ...state.editor,
        structuredOutput: [
          ...state.editor.structuredOutput,
          { fieldName: "", fieldType: "string", description: "", required: false },
        ],
      },
      isDirty: true,
    })),

  updateStructuredOutputField: (index, partial) =>
    set((state) => {
      const fields = [...state.editor.structuredOutput];
      fields[index] = { ...fields[index], ...partial };
      return { editor: { ...state.editor, structuredOutput: fields }, isDirty: true };
    }),

  removeStructuredOutputField: (index) =>
    set((state) => ({
      editor: {
        ...state.editor,
        structuredOutput: state.editor.structuredOutput.filter((_, i) => i !== index),
      },
      isDirty: true,
    })),
}));

export default useSkillsStore;
